import React, { useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  useMediaQuery,
  IconButton,
  Collapse,
  Tooltip,
  Chip,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { sidebarConfig, SidebarItem } from "../../../userConfiguration/sidebar";
import type { RootState } from "../../../redux/store";

interface SidebarProps {
  open?: boolean;
  onClose?: () => void;
  collapsed?: boolean;
}

const SidebarListItem: React.FC<{
  item: SidebarItem;
  collapsed: boolean;
  onClose?: () => void;
  level?: number;
}> = ({ item, collapsed, onClose, level = 0 }) => {
  const theme = useTheme();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const Icon = item.icon;

  // The monitoring item carries a live count of ongoing incidents rather than
  // a static badge from the sidebar config.
  const ongoingIncidents = useSelector((state: RootState) => state.monitor.ongoingIncidents);
  const isIncidentBadge = item.checkSidebar === 'server-monitoring';
  const badge = isIncidentBadge ? ongoingIncidents : item.badge;

  const isActive = location.pathname === item.path || 
    location.pathname.startsWith(item.path + '/') ||
    (item.children && item.children.some(child => 
      location.pathname === child.path || location.pathname.startsWith(child.path + '/')
    ));

  // const isChildActive = item.children && item.children.some(child => 
  //   location.pathname === child.path || location.pathname.startsWith(child.path + '/')
  // );

  const handleClick = () => {
    if (item.children) {
      setOpen(!open);
    } else if (onClose) {
      onClose();
    }
  };

  const listItemContent = (
    <ListItemButton
      onClick={handleClick}
      component={item.children ? "div" : Link}
      to={item.children ? undefined : item.path}
      disabled={item.disabled}
      sx={{
        minHeight: 48,
        justifyContent: collapsed ? "center" : "flex-start",
        px: collapsed ? 2.5 : 3,
        pl: collapsed ? 2.5 : 3 + level * 2,
        color: theme.palette.text.primary,
        backgroundColor: isActive ? theme.palette.primary.main + '20' : 'transparent',
        borderLeft: isActive ? `3px solid ${theme.palette.primary.main}` : 'none',
        "&:hover": {
          bgcolor: isActive 
            ? theme.palette.primary.main + '30' 
            : theme.palette.action.hover,
        },
        "&.Mui-disabled": {
          opacity: 0.5,
        },
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: 0,
          mr: collapsed ? 0 : 2,
          justifyContent: "center",
          color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
        }}
      >
        <Icon />
      </ListItemIcon>

      {!collapsed && (
        <>
          <ListItemText
            primary={item.title}
            secondary={item.description}
            sx={{
              "& .MuiTypography-root": {
                color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
              },
              "& .MuiListItemText-secondary": {
                fontSize: "0.75rem",
                color: theme.palette.text.secondary,
              },
            }}
          />
          {badge != null && badge !== 0 && badge !== '' && (
            <Chip
              label={badge}
              size="small"
              color={isIncidentBadge ? "error" : "primary"}
              aria-label={isIncidentBadge ? `${badge} servers down` : undefined}
              sx={{
                ml: 1, 
                height: 20, 
                fontSize: "0.7rem",
                "& .MuiChip-label": {
                  px: 1,
                }
              }}
            />
          )}
          {item.children && (
            <Box component="span" sx={{ ml: "auto", color: theme.palette.text.primary }}>
              {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
          )}
        </>
      )}
    </ListItemButton>
  );

  return (
    <>
      <ListItem
        disablePadding
        sx={{
          display: "block",
          width: "100%",
        }}
      >
        {collapsed ? (
          <Tooltip 
            title={item.title} 
            placement="right"
            arrow
          >
            {listItemContent}
          </Tooltip>
        ) : (
          listItemContent
        )}
      </ListItem>

      {item.children && !collapsed && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List
            component="div"
            disablePadding
            sx={{
              bgcolor: theme.palette.action.hover + '20',
            }}
          >
            {item.children.map((child) => (
              <SidebarListItem
                key={child.path}
                item={child}
                collapsed={collapsed}
                onClose={onClose}
                level={level + 1}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const Sidebar: React.FC<SidebarProps> = ({
  open = true,
  onClose,
  collapsed = false,
}) => {
  const isMobile = useMediaQuery("(max-width:1199px)");
  const theme = useTheme();

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: collapsed ? 73 : 280,
          bgcolor: theme.palette.background.paper,
          border: "none",
          py: 1,
          top: "64px", // Position below fixed navbar
          height: "calc(100% - 64px)", // Account for navbar height
          position: "fixed",
          boxShadow: theme.shadows[1],
          "& .MuiDrawer-paper": {
            border: "none",
            top: "64px", // Ensure paper is also positioned correctly
            height: "calc(100% - 64px)",
          },
        },
      }}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile.
      }}
    >
      {isMobile && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            p: 1,
            bgcolor: theme.palette.background.paper,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <IconButton 
            onClick={onClose} 
            sx={{ color: theme.palette.text.primary }}
            aria-label="Close sidebar"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      <List
        sx={{
          p: 0,
          bgcolor: theme.palette.background.paper,
          height: "100%",
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: theme.palette.divider,
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: theme.palette.text.secondary,
          },
        }}
        component="nav"
        aria-label="Main navigation"
      >
        {sidebarConfig.map((item) => (
          <SidebarListItem
            key={item.path}
            item={item}
            collapsed={collapsed}
            onClose={onClose}
          />
        ))}
      </List>
    </Drawer>
  );
};

export default Sidebar;
